<template>
    <KActionBar
        class="package-sticky-cta"
        label="Booking"
        :visible="visible"
    >
        <div class="package-sticky-cta__pitch">
            <p class="package-sticky-cta__headline">
                {{ headline }}
            </p>
            <p class="package-sticky-cta__support">
                {{ support }}
            </p>
        </div>

        <template #actions>
            <KButton
                type="button"
                size="lg"
                label="Plan This Holiday"
                @click="emit('book')"
            >
                <template #icon-end>
                    <Icon name="tabler:arrow-right" />
                </template>
            </KButton>
        </template>
    </KActionBar>
</template>

<script setup>
import { KActionBar, KButton } from '@vemedia/karibu'
import { computed } from 'vue'

// The persistent call to action that replaced the sticky summary. It leads with the ask
// rather than with the package name — the visitor is already on the page, so repeating the
// title here would only crowd out the thing they are meant to do next.
//
// One action on purpose: "Ask an Expert" is in the title block and the sidebar widget
// offers WhatsApp directly, so a second button here would only dilute this one.
const props = defineProps({
    /** A `usePackageQuote()` instance, shared with every other surface on the page. */
    quote: {
        type: Object,
        required: true,
    },
    visible: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['book'])

const { canBook, total, deposit, depositPercentage, fromPriceLabel } = props.quote

const hasEstimate = computed(() => canBook.value && total.value > 0)

// Before the quote is complete the bar sells the next step; once it is, the visitor's own
// numbers are the most persuasive thing on it, so the two lines swap roles.
const headline = computed(() => (hasEstimate.value
    ? `Your estimate: $${total.value}`
    : 'Choose your dates and see the total'))

const support = computed(() => {
    if (hasEstimate.value) {
        return depositPercentage > 0
            ? `${depositPercentage}% deposit — $${deposit.value} to confirm`
            : 'We’ll confirm availability before anything is booked'
    }

    return fromPriceLabel.value
        ? `${fromPriceLabel.value.charAt(0).toUpperCase()}${fromPriceLabel.value.slice(1)}`
        : 'We’ll confirm availability before anything is booked'
})
</script>

<style lang="scss" scoped>
@use '@vemedia/bcn-components/scss/responsive' as *;

// The bar's own surface stays neutral: the single accent in this composition is the button,
// which is what makes it read as the thing to do rather than as page chrome.
.package-sticky-cta {
    --action-bar-max-width: var(--container-4);

    &__pitch {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
        min-width: 0;
    }

    &__headline {
        margin: 0;
        color: var(--color-text);
        font-size: var(--font-size-4);
        font-weight: var(--font-weight-semibold);
        line-height: var(--line-height-3);
        text-wrap: balance;
    }

    &__support {
        margin: 0;
        color: var(--color-text-muted);
        font-size: var(--font-size-2);
        line-height: var(--line-height-2);
    }

    // Keep the button clear of the third-party chat launcher: the layout pins an 84px
    // square to this corner at a z-index far above the bar, so without this its last few
    // pixels swallow clicks meant for the primary action. Needed at every width — the bar's
    // content is right-aligned to its own maximum, not to the viewport.
    :deep(.k-action-bar__inner) {
        padding-inline-end: 6rem;
    }

    @include mobile {
        &__headline {
            font-size: var(--font-size-3);
        }

        :deep(.k-action-bar__actions button) {
            flex: 1 1 auto;
        }
    }
}
</style>
